/**
 * Track lists over the wire — one action per call, acknowledged.
 *
 * `api/journal.ts`'s shape, deliberately, and for its reason: these are
 * **row-shaped user records**, not a document. `usePersistentWorkspace`
 * coalesces, debounces and shows a failure without retrying — right for a
 * workspace, where the next whole-state write overwrites the failure anyway, and
 * wrong here. Nothing overwrites "I added MSFT to Swing"; a dropped write leaves
 * the symbol out while the star is drawn filled.
 *
 * So every call **throws** on failure, and the caller rolls its optimistic
 * update back and says so. A star that silently stays filled over a failed write
 * is precisely the bug this file exists to prevent.
 *
 * Returns the stored list rather than void, because the server normalises: a
 * hot key may move off another list, a create may take the crown, an add drops
 * duplicates and keeps the original `addedAt`. A client that guessed at those
 * would drift from what is actually stored — and the drift shows up as a star
 * that is filled in one tab and hollow in the next.
 */

import { currentUserId, requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

/** qv-data's base URL — the same one the workspace store uses. Empty disables. */
const BASE = httpBase(import.meta.env.VITE_QV_SET_URL as string | undefined);

/** One symbol in a list, with the two facts that make a list readable later. */
export interface TrackListMember {
  symbol: string;
  /** ISO timestamp. */
  addedAt: string | null;
  /**
   * What it was trading at when it went in, or null.
   *
   * Null is **unknown, never zero**: added from the symbol search there is no
   * price on screen and none is sent. Anything deriving a "since added" figure
   * must skip the row rather than divide by it.
   */
  addedClose: number | null;
}

export interface TrackList {
  /** The ULID. `code` is what the screener addresses it by. */
  id: string;
  /** `LIST_<id>` — the group code, so a list is pickable like SP500. */
  code: string;
  name: string;
  /** `1`–`9`, or null. Never a letter — see `lib/track-lists.ts`. */
  hotKey: string | null;
  /** A palette slot (`amber`, `violet`, …), resolved against the theme here. */
  colour: string;
  displayOrder: number;
  /** The alt-click target. At most one per user. */
  isDefault: boolean;
  members: TrackListMember[];
  memberCount: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface TrackListCatalog {
  lists: TrackList[];
  /**
   * The palette and key set the **server** will accept.
   *
   * Carried rather than duplicated in the client: a slot renamed on one side and
   * not the other is a 400 nobody can explain from the dialog that caused it.
   */
  colours: string[];
  hotKeys: string[];
  maxLists: number;
  maxMembers: number;
}

/** Whether lists are available at all — no server configured means no feature. */
export function trackListsEnabled(): boolean {
  return BASE !== "";
}

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  if (!BASE) throw new Error("no list server configured");
  // Whose lists, threaded here rather than written into each path — the same
  // call `api/journal.ts` makes, so a route added later cannot be one omission
  // away from reading somebody else's.
  const separator = path.includes("?") ? "&" : "?";
  const url = `${BASE}${path}${separator}userId=${encodeURIComponent(currentUserId())}`;
  const auth = await requestHeaders();
  const response = await fetch(url, {
    ...init,
    // After `init`: a caller passing its own headers would otherwise drop the
    // token, which fails as a 401 nowhere near the call that caused it.
    headers: { "content-type": "application/json", ...auth, ...(init?.headers ?? {}) },
  });
  if (!response.ok) {
    // A broken rule comes back as 400 and a sentence — "you already have a list
    // called AI" — which is worth showing verbatim. Anything else is an outage.
    const detail = await response.json().catch(() => null);
    throw new Error(detail?.detail ?? `lists ${response.status}`);
  }
  return (await response.json()) as T;
}

const post = <T>(path: string, body: unknown): Promise<T> =>
  call<T>(path, { method: "POST", body: JSON.stringify(body) });

/** Every list, with its membership. One payload answers a whole screen. */
export const fetchTrackLists = (): Promise<TrackListCatalog> =>
  call<TrackListCatalog>("/lists");

export interface CreateListInput {
  /** Client-minted ULID, so the star can fill before the round trip. */
  id: string;
  name: string;
  hotKey?: string | null;
  colour?: string;
  isDefault?: boolean;
  symbols?: string[];
  closes?: Record<string, number>;
}

export const createTrackList = (input: CreateListInput): Promise<TrackList> =>
  post<TrackList>("/lists", input);

export interface UpdateListInput {
  name?: string;
  hotKey?: string;
  colour?: string;
  isDefault?: boolean;
  /** `hotKey` absent means unchanged, so removing one needs its own word. */
  clearHotKey?: boolean;
}

export const updateTrackList = (id: string, patch: UpdateListInput): Promise<TrackList> =>
  post<TrackList>(`/lists/${encodeURIComponent(id)}`, patch);

export const deleteTrackList = (id: string): Promise<{ id: string; deleted: boolean }> =>
  call(`/lists/${encodeURIComponent(id)}`, { method: "DELETE" });

/**
 * Add symbols. Idempotent, and a symbol already in keeps its original stamp.
 *
 * `closes` is what the caller saw on screen — the honest reading of "close at
 * the time of add". The grid has it in the row that was clicked; the symbol
 * search does not, and sends nothing.
 */
export const addToTrackList = (
  id: string,
  symbols: string[],
  closes?: Record<string, number>,
): Promise<TrackList> =>
  post<TrackList>(`/lists/${encodeURIComponent(id)}/add`, { symbols, closes });

export const removeFromTrackList = (id: string, symbols: string[]): Promise<TrackList> =>
  post<TrackList>(`/lists/${encodeURIComponent(id)}/remove`, { symbols });

export const reorderTrackLists = (ids: string[]): Promise<{ lists: TrackList[] }> =>
  post<{ lists: TrackList[] }>("/lists/reorder", { ids });

export const removeFromAllTrackLists = (
  symbols: string[],
): Promise<{ lists: TrackList[] }> =>
  post<{ lists: TrackList[] }>("/lists/remove-all", { symbols });
